import {
  ProfileMessage,
  ProfileMessageType,
  websocket,
  Profile,
  MatchmakingMessageType,
  InvitationMessageType,
} from '@playcastdotio/Messaging';
import { sendMessage } from '..';
import { profileState, ownerAliasState, playerNameState, matchmakeToPublicMachineState } from '../../../stores/lobbyStores';
import { authStore, loginProfileState, networkMetricsState, regionOverrideState } from '../../../stores/globalStores';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';
import { getPersistentPlayerId } from '../../..';
import { saveDisplayName } from '../../player';
import { sendAnalytics, PlaycastAnalyticsLobbyCreated, PlaycastAnalyticsLobbyEntered } from '@playcastdotio/Analytics';
import { invitationDiagnosticsCache } from '../invitationDiagnosticsCache';

// Race condition handling for invitations list (lobbies)
let waitingForInvitationsResult = false;
let invitationsResultMarkedDirty = false;
let lastInvitationsRequestParams: {
  alias: string;
  platform: IdentityPlatform;
  cursor?: string;
  limit: number;
  search?: string;
} | null = null;

let shouldIgnoreNextProfileInvitationsResponse = false;
let pendingProfileInvitationsRequest: {
  alias: string;
  platform: IdentityPlatform;
  cursor?: string;
  limit: number;
  search?: string;
} | null = null;

const unsubscribeFromInvitations = (invitationIds: string[]) => {
  if (invitationIds.length === 0) return;

  sendMessage(
    websocket.generators.invitation(InvitationMessageType.INVITATION_UNSUBSCRIBE, {
      invitationIds,
    }),
  );
};

// State for tracking retry attempts
type RetryState = {
  attempts: number;
  startTime: number;
  alias: string;
  platform: IdentityPlatform;
  timeoutId?: NodeJS.Timeout;
};

let retryState: RetryState | null = null;

// Helper function to check if the current profile request is for the owner
const isOwnerProfile = (alias: string, platform: IdentityPlatform): boolean => {
  console.log('isOwnerProfile check:', { alias, platform });
  const ownerAliases = ownerAliasState.currentState.aliases;
  console.log('ownerAliases:', ownerAliases);

  // If aliases aren't loaded yet, assume it's an owner profile to be safe
  if (!ownerAliases || ownerAliases.default === '') {
    console.log('Aliases not loaded yet, assuming owner profile');
    return true;
  }

  // Check if the requested alias matches any of the owner's aliases for the given platform
  const platformAliases = ownerAliases[platform];
  console.log('platformAliases for', platform, ':', platformAliases);
  if (platformAliases && platformAliases.includes(alias.toLowerCase())) {
    console.log('Profile IS owner - alias found in platform aliases');
    return true;
  }

  console.log('Profile is NOT owner', { alias: alias.toLowerCase(), platform, ownerAliases });
  return false;
};

// Helper function to retry profile query
const retryProfileQuery = (alias: string, platform: IdentityPlatform, attempt: number) => {
  const RETRY_DELAY = 500; // 500ms delay between retries
  const MAX_RETRY_TIME = 10000; // 10 seconds max retry time

  if (!retryState || Date.now() - retryState.startTime > MAX_RETRY_TIME) {
    // Stop retrying after 10 seconds
    // console.log('Profile retry timeout reached, showing error');
    profileState.updateState({
      ...profileState.currentState,
      profile: null,
      isLoading: false,
      error: 'Profile not found',
    });
    retryState = null;
    return;
  }

  // console.log(`Retrying profile query for ${alias} (attempt ${attempt})`);

  // Stop after 10 attempts.
  if (attempt > 10) {
    // console.log('Profile retry limit reached, showing error');
    retryState = null;
    return;
  }

  retryState.timeoutId = setTimeout(() => {
    sendMessage(
      websocket.generators.profile(ProfileMessageType.PROFILE_GET, {
        alias: alias.toLowerCase(),
        platform: platform,
      }),
    );
  }, RETRY_DELAY);
};

export const handleProfileMessage = (message: ProfileMessage) => {
  // console.log('Profile message:', message);
  switch (message.type) {
    case ProfileMessageType.PROFILE_GET_RESPONSE: {
      // console.log('Profile get response:', message.profile);

      // Clear retry state on successful response
      if (retryState?.timeoutId) {
        clearTimeout(retryState.timeoutId);
      }
      retryState = null;

      console.log('PROFILE_GET_RESPONSE: Setting isOwner to:', message.isOwner);
      console.log('PROFILE_GET_RESPONSE: Current isOwner:', profileState.currentState.isOwner);

      // Preserve existing isOwner if the new response is undefined (race condition fix)
      const preservedIsOwner = message.isOwner !== undefined ? message.isOwner : profileState.currentState.isOwner;
      console.log('PROFILE_GET_RESPONSE: Using isOwner:', preservedIsOwner);

      profileState.updateState({
        ...profileState.currentState,
        profile: message.profile || null,
        isOwner: preservedIsOwner,
        isLoading: false,
        error: message.profile ? null : 'Profile not found',
      });
      break;
    }
    case ProfileMessageType.PROFILE_UPDATE_SUCCESS: {
      if (message.profile) {
        profileState.updateState({
          ...profileState.currentState,
          profile: message.profile,
          isOwner: true,
          isLoading: false,
          error: null,
        });
      }
      // console.log('Profile update success');
      break;
    }
    case ProfileMessageType.PROFILE_GET_ERROR: {
      console.log('Profile get error:', message.error);
      // For owner profile requests, implement retry logic to handle race conditions
      let shouldRetry = false;

      if (retryState && retryState.attempts < 10) {
        // console.log('[ProfileMessage] Checking retry state:', {
        //   alias: retryState.alias,
        //   platform: retryState.platform,
        //   attempts: retryState.attempts,
        // });

        // We have an active retry state, check if we should continue retrying
        shouldRetry = isOwnerProfile(retryState.alias, retryState.platform);
        // console.log('[ProfileMessage] Should retry?', shouldRetry);

        if (shouldRetry) {
          retryState.attempts++;
          // console.log('[ProfileMessage] Retrying profile query, attempt:', retryState.attempts);
          retryProfileQuery(retryState.alias, retryState.platform, retryState.attempts);

          // Keep loading state active during retries
          profileState.updateState({
            ...profileState.currentState,
            profile: null,
            isLoading: true, // Keep loading state
            error: null, // Don't show error during retries
          });
          // console.log('[ProfileMessage] Updated profile state for retry');
          break;
        } else {
          // Clear retry state if we shouldn't retry
          // console.log('[ProfileMessage] Not retrying, clearing retry state');
          if (retryState.timeoutId) {
            clearTimeout(retryState.timeoutId);
          }
          retryState = null;
        }
      }

      // Default error handling for non-owner profiles or when retries are exhausted
      profileState.updateState({
        ...profileState.currentState,
        profile: null,
        isLoading: false,
        error: message.error?.message || 'Profile not found',
      });
      break;
    }
    case ProfileMessageType.PROFILE_UPDATE_ERROR: {
      console.log('Profile update error:', message.error);
      break;
    }
    case ProfileMessageType.PROFILE_GET_INVITATIONS_RESPONSE: {
      console.log('Profile get invitations response:', message.invitations);

      if (!waitingForInvitationsResult) {
        break;
      }

      if (shouldIgnoreNextProfileInvitationsResponse) {
        shouldIgnoreNextProfileInvitationsResponse = false;
        break;
      }

      pendingProfileInvitationsRequest = null;

      waitingForInvitationsResult = false;

      // If invitations result is marked dirty, re-request get profile invitations after a delay
      // to allow the server to process the lobby creation
      if (invitationsResultMarkedDirty && lastInvitationsRequestParams) {
        invitationsResultMarkedDirty = false;
        const params = lastInvitationsRequestParams;
        lastInvitationsRequestParams = null;

        // Wait a bit to allow server to process lobby creation before re-requesting
        setTimeout(() => {
          queryProfileInvitations(
            params.alias,
            params.platform,
            params.cursor,
            params.limit,
            params.search
          );
        }, 500);
        break;
      }

      lastInvitationsRequestParams = null;

      const maybeSelectedInvitationId = localStorage.getItem('lastRedirectedInvitationId');
      localStorage.removeItem('lastRedirectedInvitationId');
      const selectedInvitation = message.invitations.find((invitation) => invitation.id === maybeSelectedInvitationId);

      // Find invitations that are no longer in the list
      const oldInvitationIds = profileState.currentState.invitations.map((inv) => inv.id);
      const newInvitationIds = message.invitations.map((inv) => inv.id);
      const removedInvitationIds = oldInvitationIds.filter((id) => !newInvitationIds.includes(id));

      // Cache removed invitations for diagnostics
      const removedInvitations = profileState.currentState.invitations.filter((inv) => removedInvitationIds.includes(inv.id));
      removedInvitations.forEach((invitation) => {
        console.log('Caching removed invitation for diagnostics:', invitation.id);
        invitationDiagnosticsCache.set(invitation.id, invitation, 'removed');
      });
      // Cache updated invitations for diagnostics (invitations that exist in both old and new lists)
      const updatedInvitationIds = oldInvitationIds.filter((id) => newInvitationIds.includes(id));
      const updatedInvitations = message.invitations.filter((inv) => updatedInvitationIds.includes(inv.id));
      updatedInvitations.forEach((invitation) => {
        // Only cache if the invitation content has actually changed
        const oldInvitation = profileState.currentState.invitations.find((oldInv) => oldInv.id === invitation.id);
        if (oldInvitation && JSON.stringify(oldInvitation) !== JSON.stringify(invitation)) {
          console.log('Caching updated invitation for diagnostics:', invitation.id);
          invitationDiagnosticsCache.set(invitation.id, oldInvitation, 'updated'); // Cache the OLD version before update
        }
      });

      // Unsubscribe from removed invitations
      unsubscribeFromInvitations(removedInvitationIds);

      console.log('PROFILE_GET_INVITATIONS_RESPONSE: isOwner before update:', profileState.currentState.isOwner);
      profileState.updateState({
        ...profileState.currentState,
        invitations: message.invitations || [],
        nextCursor: message.nextCursor,
        invitationsLoading: false,
        invitationsError: undefined,
        selectedInvitationId: selectedInvitation?.id || profileState.currentState.selectedInvitationId,
      });
      console.log('PROFILE_GET_INVITATIONS_RESPONSE: isOwner after update:', profileState.currentState.isOwner);
      break;
    }
    case ProfileMessageType.PROFILE_GET_INVITATIONS_ERROR: {
      console.log('Profile get invitations error:', message.error);
      // Unsubscribe from all current invitations since we're clearing the list
      const currentInvitationIds = profileState.currentState.invitations.map((inv) => inv.id);
      unsubscribeFromInvitations(currentInvitationIds);

      profileState.updateState({
        ...profileState.currentState,
        invitations: [],
        nextCursor: undefined,
        invitationsLoading: false,
        invitationsError: message.error?.message || 'Failed to load invitations',
      });
      break;
    }
    case ProfileMessageType.PROFILE_GET_ALIASES_RESPONSE: {
      console.log('Profile get aliases response:', message.aliases);
      ownerAliasState.updateState({
        ...ownerAliasState.currentState,
        aliases: message.aliases || {},
        usernames: message.usernames || {},
      });
      // Also update the name state
      const defaultAlias = message.aliases.default;
      const defaultPlatform = Object.keys(message.aliases)
        .filter((p) => p !== 'default')
        .find((key) => message.aliases[key] === defaultAlias);
      if (defaultPlatform) {
        const name = message.usernames[defaultPlatform];
        if (name) {
          playerNameState.updateState({
            name,
          });
          saveDisplayName();
        }
      }
      break;
    }
    case ProfileMessageType.PROFILE_GET:
    case ProfileMessageType.PROFILE_UPDATE:
    case ProfileMessageType.PROFILE_GET_INVITATIONS: {
      console.warn('Server received message it should not have received');
      console.log(message);
      break;
    }
  }
};

let debounceTimeout: NodeJS.Timeout | null = null;

export const queryProfile = (alias: string, platform?: IdentityPlatform) => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
  console.log('[ProfileMessage] Querying profile:', { alias, platform });

  debounceTimeout = setTimeout(() => {
    const targetPlatform = platform || IdentityPlatform.CLERK;

    // Clear any existing retry state
    if (retryState?.timeoutId) {
      clearTimeout(retryState.timeoutId);
    }

    // Initialize retry state if this is an owner profile request
    if (isOwnerProfile(alias, targetPlatform)) {
      retryState = {
        attempts: 0,
        startTime: Date.now(),
        alias: alias.toLowerCase(),
        platform: targetPlatform,
      };
      console.log('Initializing retry state for owner profile:', alias);
    } else {
      console.log('[ProfileMessage] Not retrying, clearing retry state');
      retryState = null;
    }

    profileState.updateState({
      ...profileState.currentState,
      isLoading: true,
      error: null,
    });

    sendMessage(
      websocket.generators.profile(ProfileMessageType.PROFILE_GET, {
        alias: alias.toLowerCase(),
        platform: targetPlatform,
      }),
    );
  }, 50);
};

export const updateProfile = (profile: Profile) => {
  if (!authStore.currentState.playerAuthId) {
    console.error('No player auth ID found');
    return;
  }

  sendMessage(
    websocket.generators.profile(ProfileMessageType.PROFILE_UPDATE, {
      profile,
    }),
  );
};

export const queryProfileInvitations = (alias: string, platform?: IdentityPlatform, cursor?: string, limit = 16, search?: string) => {
  waitingForInvitationsResult = true;
  invitationsResultMarkedDirty = false;

  // Store request params for potential re-request
  lastInvitationsRequestParams = {
    alias: alias.toLowerCase(),
    platform: platform || IdentityPlatform.CLERK,
    cursor,
    limit,
    search,
  };

  profileState.updateState({
    ...profileState.currentState,
    invitationsLoading: profileState.currentState.invitations?.length > 0,
    invitationsError: undefined,
  });
  sendMessage(
    websocket.generators.profile(ProfileMessageType.PROFILE_GET_INVITATIONS, {
      alias: alias.toLowerCase(),
      platform: platform || IdentityPlatform.CLERK,
      cursor,
      limit,
      invitationType: 'cafe',
      search,
    }),
  );
};

export const createMatchmakerLobby = () => {
  // if (!authStore.currentState.playerAuthId) {
  //   console.error('No player auth ID found');
  //   return;
  // }

  if (waitingForInvitationsResult) {
    invitationsResultMarkedDirty = true;
  }

  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_CREATE_INVITATION, {
    playerInfo: {
      displayName: profileState.currentState.profile?.displayName || '',
      pingMap: networkMetricsState.currentState.ping,
    },
    matchmakeToPublicMachine: matchmakeToPublicMachineState.currentState.matchmakeToPublicMachine,
    sessionPreset: {
      authRequired: false,
      defaultPlayerSettings: {
        gamepad: true,
        keyboard: false,
        mouse: false,
      },
    },
  });
  sendMessage(msg);

  sendAnalytics({
    name: 'Lobby Created',
    role: 'host',
    persistentId: getPersistentPlayerId(),
    hostAuthId: authStore.currentState.playerAuthId,
    lobbyId: profileState.currentState.selectedInvitationId,
    profileAlias: profileState.currentState.profile?.displayName || '',
    source: 'browser',
  } as PlaycastAnalyticsLobbyCreated);
};

export const joinMatchmakerLobby = () => {
  if (!profileState.currentState.selectedInvitationId) {
    console.error('No invitation ID found');
    return;
  }
  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_JOIN_INVITATION, {
    invitationId: profileState.currentState.selectedInvitationId,
    playerInfo: {
      displayName: loginProfileState.currentState.name || playerNameState.currentState.name || 'Anonymous',
      playerId: authStore.currentState.playerAuthId || getPersistentPlayerId(),
      pingMap: networkMetricsState.currentState.ping,
    },
  });
  sendMessage(msg);

  sendAnalytics({
    name: 'Lobby Entered',
    role: 'host',
    persistentId: getPersistentPlayerId(),
    playerAuthId: authStore.currentState.playerAuthId,
    lobbyId: profileState.currentState.selectedInvitationId,
    entryMethod: 'browser lobby card',
  } as PlaycastAnalyticsLobbyEntered);
};

export const leaveMatchmakerLobby = () => {
  if (!profileState.currentState.selectedInvitationId) {
    console.error('No invitation ID found');
    return;
  }
  // Clear our matchFoundId and matchBroadcastId
  profileState.updateState({
    ...profileState.currentState,
    matchFoundId: undefined,
    matchBroadcastId: undefined,
  });

  // Check we are actually in the invitation.
  const selectedInvitation = profileState.currentState.invitations.find(
    (invitation) => invitation.id === profileState.currentState.selectedInvitationId,
  );
  const persistentId = getPersistentPlayerId();
  const inInvitation =
    selectedInvitation?.type === 'cafe' &&
    (selectedInvitation.manifest.players.some(
      (player) => player.playerId === authStore.currentState.playerAuthId || player.playerId === persistentId,
    ) ||
      selectedInvitation.owner?.profileId === authStore.currentState.playerAuthId);
  if (!inInvitation) {
    console.error('We are not in the invitation');
    return;
  }

  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_LEAVE_INVITATION, {
    invitationId: profileState.currentState.selectedInvitationId,
    playerId: authStore.currentState.playerAuthId || getPersistentPlayerId(),
  });
  sendMessage(msg);
};

export const startMatchmakerLobby = (useSteamGuestLogin?: boolean, regionOverride?: string) => {
  if (!profileState.currentState.selectedInvitationId) {
    console.error('No invitation ID found');
    return;
  }
  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_ADD_TO_QUEUE, {
    invitationId: profileState.currentState.selectedInvitationId,
    useSteamGuestLogin,
    regionOverride: regionOverride || regionOverrideState.currentState.regionOverride,
  });
  sendMessage(msg);
};

export const destroyMatchmakerLobby = (selectedInvitationId?: string) => {
  if (!selectedInvitationId) {
    console.error('No invitation ID found');
    return;
  }
  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION, {
    invitationId: selectedInvitationId,
    reason: 'Host destroyed lobby',
  });
  sendMessage(msg);
};

export const queryOwnerAliases = () => {
  if (!authStore.currentState.playerAuthId) {
    console.error('No player auth ID found');
    return;
  }
  const msg = websocket.generators.profile(ProfileMessageType.PROFILE_GET_ALIASES, {});
  sendMessage(msg);
};

export const reRequestProfileInvitations = () => {
  if (pendingProfileInvitationsRequest) {
    const requestParams = { ...pendingProfileInvitationsRequest };
    pendingProfileInvitationsRequest = null;
    queryProfileInvitations(
      requestParams.alias,
      requestParams.platform,
      requestParams.cursor,
      requestParams.limit,
      requestParams.search
    );
  }
};

// Export cleanup function for retry state
export const cleanupProfileRetries = () => {
  if (retryState?.timeoutId) {
    clearTimeout(retryState.timeoutId);
  }
  retryState = null;

  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    debounceTimeout = null;
  }
};
